// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import type { AxiosError } from 'axios';

import { extractApiErrorMessage } from './errorMessage';

export const QUOTA_EXCEEDED_EVENT = 'app:quota-exceeded';

export type QuotaExceededType =
  | 'daily_messages'
  | 'model_access'
  | 'file_size'
  | 'kb_count'
  | 'context_length'
  | 'feature';

export type QuotaExceededPayload = {
  quotaType: string;
  message: string;
  params: Record<string, string | number>;
};

function readHeader(headers: Record<string, unknown> | undefined, key: string): string | undefined {
  if (!headers) return undefined;
  const direct = headers[key];
  if (typeof direct === 'string' && direct.trim()) return direct.trim();

  const lowerKey = key.toLowerCase();
  for (const [headerKey, value] of Object.entries(headers)) {
    if (headerKey.toLowerCase() !== lowerKey) continue;
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

function parseNumber(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function extractQuotaParams(
  quotaType: string,
  headers: Record<string, unknown> | undefined
): Record<string, string | number> {
  const params: Record<string, string | number> = {};

  if (quotaType === 'daily_messages') {
    const limit = parseNumber(readHeader(headers, 'x-quota-limit'));
    if (limit !== undefined) params.limit = limit;
  }

  if (quotaType === 'model_access') {
    const model = readHeader(headers, 'x-quota-model');
    if (model) params.model = model;
  }

  if (quotaType === 'file_size' || quotaType === 'kb_count') {
    const max = parseNumber(readHeader(headers, 'x-quota-max'));
    if (max !== undefined) params.max = max;
  }

  if (quotaType === 'context_length') {
    const max = parseNumber(readHeader(headers, 'x-quota-max'));
    if (max !== undefined) params.max = max;
  }

  if (quotaType === 'feature') {
    const feature = readHeader(headers, 'x-quota-feature');
    if (feature) params.feature = feature;
  }

  return params;
}

/**
 * Quick predicate: is this error a 429 with an x-quota-type header?
 * Used by classifyApiError to distinguish quota_exceeded from rate_limited.
 */
export function isQuotaExceededError(error: unknown): boolean {
  if (error === null || error === undefined) return false;
  const record = typeof error === 'object' && error !== null ? (error as Record<string, unknown>) : null;
  if (!record) return false;
  const response = typeof record.response === 'object' && record.response !== null
    ? (record.response as Record<string, unknown>)
    : null;
  if (!response || response.status !== 429) return false;
  const headers = typeof response.headers === 'object' && response.headers !== null
    ? (response.headers as Record<string, unknown>)
    : {};
  return Boolean(readHeader(headers, 'x-quota-type'));
}

export function buildQuotaExceededPayload(error: AxiosError): QuotaExceededPayload | null {
  if (error.response?.status !== 429) return null;
  const headers = (error.response.headers ?? {}) as Record<string, unknown>;
  const quotaType = readHeader(headers, 'x-quota-type');
  if (!quotaType) return null;

  return {
    quotaType,
    message: extractApiErrorMessage(error, 'Quota exceeded'),
    params: extractQuotaParams(quotaType, headers),
  };
}

export function emitQuotaExceededEvent(payload: QuotaExceededPayload): void {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(
    new CustomEvent<QuotaExceededPayload>(QUOTA_EXCEEDED_EVENT, { detail: payload })
  );
}
